import globalsObject from "./globalsObject";

import {ColorUtils} from "./colorUtils";
import {PalettenLoader} from "./palettenLoader";
import {CONSTANTS} from "./meta_data";
import {THREE} from "./resource/BVH.js";
import {ColorCodeAbhaengigkeitsArten} from "./databaseInterfaceClasses";
import type {PalettenEintragFromDB} from "./databaseInterfaceClasses"
import DEBUG from "./DEBUG";
import {arrayElementByKeyValue, arrayIndexByKeyValue} from "../global/arrayHelper";

export interface ColorConstructorArgument {
    colorCode : string;
    colorCodeToDisplay? : string;
    colorValue:number;
    palette : string;
    palettenCluster : string;
    isVisible : boolean;
    colorCodeNr : number;
    farbname : string;
    blackWhiteOnly : boolean;
    isDependent : boolean;
    dependentOn : string;
    dependentColors? : Color[];
    groupID;
    inKonfi : boolean;
    isHideable : boolean;
    blendToBackground?;
    multipleMasters : boolean;
    percentages;
    masterColors?;
    hatPattern? : boolean;
    farbAbhaengigkeitsArt?:ColorCodeAbhaengigkeitsArten
}

class Mindata {
    public abstand = Infinity;
    public palettenIndex = -1;
    public referencedColorCode:Color|null = null;
    public deltaE = Infinity;
}

export class Color {
    colorCode : string;
    colorCodeToDisplay : string
    colorValue : number;
    palette : string;
    palettenCluster : string;
    isVisible : boolean;
    colorCodeNr : number;
    farbname : string;
    blackWhiteOnly : boolean;
    isDependent : boolean;
    dependentOn : string;
    dependentColors : Color[];
    groupID:string;
    inKonfi : boolean;
    isHideable : boolean;
    blendToBackground;
    multipleMasters : boolean;
    percentages;
    masterColors;
    hatPattern : boolean;
    farbAbhaengigkeitsArt:ColorCodeAbhaengigkeitsArten;

    constructor(parameters:ColorConstructorArgument) {
        this.colorCode = parameters.colorCode;
        this.colorCodeToDisplay = parameters.colorCodeToDisplay || this.colorCode;
        this.colorValue = parameters.colorValue;
        this.isVisible = parameters.isVisible || true;
        this.colorCodeNr = parameters.colorCodeNr;
        this.farbname = parameters.farbname || "";
        this.blackWhiteOnly = parameters.blackWhiteOnly || false;
        this.isDependent = parameters.isDependent || false;
        this.dependentOn = parameters.dependentOn || "";
        this.dependentColors = [];
        this.groupID = parameters.groupID;
        this.inKonfi = parameters.inKonfi || false;
        this.isHideable = parameters.isHideable || false;
        this.blendToBackground = parameters.blendToBackground || false;
        this.multipleMasters = parameters.multipleMasters || false;
        this.percentages = parameters.percentages || {};
        this.palette = parameters.palette || "";
        this.palettenCluster = parameters.palettenCluster || CONSTANTS.STANDARD_PALETTENCLUSTER;
        this.hatPattern = parameters.hatPattern || false;
        this.farbAbhaengigkeitsArt = parameters.farbAbhaengigkeitsArt || ColorCodeAbhaengigkeitsArten.NONE;
    }

    public clone():Color {
        return new  Color(
            {
                colorCode: this.colorCode,
                colorCodeToDisplay : this.colorCodeToDisplay,
                colorValue: this.colorValue,
                palette: this.palette,
                palettenCluster: this.palettenCluster,
                isVisible: this.isVisible,
                colorCodeNr: this.colorCodeNr,
                farbname: this.farbname,
                blackWhiteOnly: this.blackWhiteOnly,
                isDependent: this.isDependent,
                dependentOn: this.dependentOn,
                dependentColors: this.dependentColors,
                groupID: this.groupID,
                inKonfi: this.inKonfi,
                isHideable: this.isHideable,
                blendToBackground: this.blendToBackground,
                multipleMasters: this.multipleMasters,
                percentages: this.percentages,
                masterColors: this.masterColors,
                farbAbhaengigkeitsArt: this.farbAbhaengigkeitsArt,
                hatPattern:this.hatPattern
            }
        );
    }

    getValueAsHex = ()=>ColorUtils.decToHex(this.colorValue);

    setValueFromHex(hexValue:string, palette:string|null=null) {
        this.colorValue = ColorUtils.hexToDec(hexValue);
        this.palette = palette || this.palette;
        this.setDependentColors();
    }

    createVec4():THREE.Vector4 {
        const aValue = ColorUtils.hexToRgbObject(this.getValueAsHex()) as {r,g,b};
        return new THREE.Vector4(aValue.r / 255, aValue.g / 255, aValue.b / 255, 1);
    }

    /*
     * Setzt schwarz oder weiß als Farbe je nachdem, welche Farbe übergeben wurde
     * ist der parameter respectExistingDependentColorValues gesetzt und true, werden Farbwerte der abhängigen Farben nur gesetzt, wenn sie NULL sind
     */
    setContrastColorTo(colorHexValue : string, palette, respectExistingDependentColorValues = false) {
        this.setDecimalValue(ColorUtils.getContrastColorToHexValue(colorHexValue, "dec"), palette);
        this.setDependentColors(respectExistingDependentColorValues);
    }

    /**
     * Setzt den Wert einer Farbe, sowie deren abhängige Farben
     * @param {number}  value                                       Dezimalzahl
     * @param palette
     * @param {boolean}    [respectExistingDependentColorValues=false]       ist dieser Parameter gesetzt und true, werden Farbwerte der abhängigen Farben nur gesetzt, wenn sie NULL sind
     */
    setDecimalValue(value:number, palette:string, respectExistingDependentColorValues = false) {
        if (typeof(value) !== "number" && value !== null) {
            value = parseInt(value, 10);
        }
        this.colorValue = value;
        this.palette = palette;
        this.setDependentColors(respectExistingDependentColorValues);
    }

    /**
     * Setzt den Farbwert aller abhängigen Farben
     * @param {boolean}    [respectExistingDependentColorValues=false]       ist dieser Parameter gesetzt und true, werden Farbwerte der abhängigen Farben nur gesetzt, wenn sie NULL sind
     */
    setDependentColors(respectExistingDependentColorValues = false) {
        this.dependentColors.forEach(depC=>{
            if (respectExistingDependentColorValues !== true || depC.colorValue === null) {
                if(depC.farbAbhaengigkeitsArt === ColorCodeAbhaengigkeitsArten.VERSTECKBAR && depC.blendToBackground === false) {
                    return;
                }
                if (depC.blackWhiteOnly) {
                    depC.setContrastColorTo(this.getValueAsHex(), this.palette);
                } else if (depC.multipleMasters === true) {
                    depC.setColorByPercentages();
                } else {
                    depC.setDecimalValue(this.colorValue, this.palette);
                }
            }
        });
    }

    getColorByPercentagMinData(palette, palettenIndex=0, minData:Mindata|null=null) {
        if(minData === null) {
            minData = new Mindata()
        }
        minData = minData as Mindata;
        if (palettenIndex >= palette.length) {
            return minData;
        }
        const rgbHex = palette[palettenIndex].RGBHex;

        for (const colorCodeName in this.percentages) {
            const colorObject = arrayElementByKeyValue(this.masterColors, "colorCode", colorCodeName);
            const gewichtungsFaktor = this.percentages[colorCodeName] / 100;
            const deltaE = ColorUtils.getDeltaEFrom2HexStrings(rgbHex, colorObject.getValueAsHex());

            const effektiverAbstand = parseInt(this.percentages[colorCodeName], 10) === 100 ? deltaE : (1 - gewichtungsFaktor) * deltaE;

            if (effektiverAbstand <= minData.abstand) {
                if (effektiverAbstand === minData.abstand) {
                    if (this.percentages[colorCodeName] < this.percentages[(<Color>minData.referencedColorCode).colorCode]) {
                        continue;
                    }
                }
                minData.abstand = effektiverAbstand;
                minData.palettenIndex = palettenIndex;
                minData.referencedColorCode = colorObject;
                minData.deltaE = deltaE;
            }

        }
        return this.getColorByPercentagMinData(palette, palettenIndex+1, minData);
    }

    getColorByPercentages(palette:Array<PalettenEintragFromDB>):string {
        let colorObject;
        const minData = this.getColorByPercentagMinData(palette);
        if (minData.deltaE > CONSTANTS.DELTA_E_THRESHHOLD) {
            colorObject = arrayElementByKeyValue(this.masterColors, "colorCode", "A");
            if (colorObject!==null){
                minData.palettenIndex = Color.getIndexOfClosestNeutralColorInPalette(colorObject.getValueAsHex(), palette);
            } else if(minData.palettenIndex === -1) {
                minData.palettenIndex= 0; //Damit zumindest kein Javascript Error passiert.
                console.error("getColorByPercentages: no minData Palettenindex for color " + this.colorCode + "; mastercolors: " + this.masterColors.length);
            }

        }
        return palette[minData.palettenIndex].RGBHex;
    }

    setColorByPercentages() {
        const palette = PalettenLoader.getFlattenedPalettenCluster(this.palettenCluster);
        if (palette === null || palette.length === 0) {
            this.setValueFromHex("#FFFFFF", null);
            globalsObject.controller.colorController.colorCouldNotBeSet(this);
            return;
        }
        const hexString = this.getColorByPercentages(palette);
        this.setValueFromHex(hexString, this.palette);
    }

    valueMatchesMasterColor(masterColor:Color|null):boolean {
        if (masterColor === null) {
            if (DEBUG) {
                console.warn("DependentColor not found: " + this.colorCode);
            }
            return true;
        }
        if (this.blackWhiteOnly) {
            if (this.colorValue !== ColorUtils.getContrastColorToHexValue(masterColor.getValueAsHex(), "dec") ||
                (this.colorValue !== 0 && this.colorValue !== ColorUtils.getContrastColorToHexValue("0", "dec"))) {
                return false;
            }
            return true;
        }
        if(this.farbAbhaengigkeitsArt === ColorCodeAbhaengigkeitsArten.CLOSEST_TO_OTHER_COLORCODES_WEIGHTED_BY_PERCENTAGES) {
            return true;
        }
        if(this.farbAbhaengigkeitsArt === ColorCodeAbhaengigkeitsArten.VERSTECKBAR && this.blendToBackground === false) {
            return true;
        }

        return this.colorValue === masterColor.colorValue;
    }

    setBlendToBackgroundFlagFromMasterColor(masterColor:Color|null) {
        let index;
        if (masterColor === null) {
            this.setValueFromHex(CONSTANTS.COLORERROR);
        } else {
            index = arrayIndexByKeyValue(masterColor.dependentColors, "colorCode", this.colorCode);
            if (this.colorValue === masterColor.colorValue) {
                this.blendToBackground = true;
                if (index === -1) {
                    masterColor.dependentColors.push(this);
                }
            } else {
                this.blendToBackground = false;
                if (index > -1) {
                    masterColor.dependentColors.splice(index, 1);
                }
            }
        }
    }

    severDependencyFrom(parentColor:Color) {
        this.isDependent = false;
        this.blackWhiteOnly = false;
        parentColor.colorNoLongerDependent(this.colorCode);
        this.dependentOn = "";
    }

    severDependencyFromMultipleMasters() {
        this.multipleMasters = false;
        this.isVisible = true;
        this.masterColors.forEach(maC=>{
            const index = arrayIndexByKeyValue(maC.dependentColors, "colorCode", this.colorCode);
            if (index > -1) {
                maC.dependentColors.splice(index, 1);
            }
        });
    }

    setDependencyTo(masterColor:Color|null) {
        if (masterColor === null || masterColor.isDependent) { //d.h. wir sind auf eine Datenbanklücke gestoßen!
            this.setValueFromHex(CONSTANTS.COLORERROR, null);
            return;
        }
        const index = arrayIndexByKeyValue(masterColor.dependentColors, "colorCode", this.colorCode);
        if (index === -1) {
            masterColor.dependentColors.push(this);
        }
        if (this.blackWhiteOnly) {
            this.setContrastColorTo(masterColor.getValueAsHex(), masterColor.palette);
        } else {
            this.setDecimalValue(masterColor.colorValue, masterColor.palette);
        }
    }

    setHideableDependencyTo(masterColor:Color|null) {
        if (masterColor === null) {
            return;
        }

        const index = arrayIndexByKeyValue(masterColor.dependentColors, "colorCode", this.colorCode);

        if (masterColor.colorValue === this.colorValue) {
            this.blendToBackground = true;
            if (index === -1) {
                masterColor.dependentColors.push(this);
            }
            return;
        }

        this.blendToBackground = false;
        if (index !== -1) {
            masterColor.dependentColors.splice(index, 1);
        }
    }

    setDependencyToMultipleMasters(masterColors:Array<Color>) {
        masterColors.forEach(mac=>{
           const index =  arrayIndexByKeyValue(mac.dependentColors, "colorCode", this.colorCode);
            if (index === -1) {
                mac.dependentColors.push(this);
            }
            return mac;
        });
        this.masterColors = masterColors;
        this.setColorByPercentages();
    }

    setValueAndBlendToBackgroundFlag(colorHexValue:string, masterColor:Color|null) {
        if (masterColor === null) {
            if (DEBUG) {
                console.log("Error! Could not find mastercolor for " + this.colorCode);
            }
            return;
        }
        const index = arrayIndexByKeyValue(masterColor.dependentColors, "colorCode", this.colorCode);
        if (colorHexValue.indexOf("none") > -1) {
            if (index === -1) {
                masterColor.dependentColors.push(this);
            }
            this.blendToBackground = true;
            this.colorValue = masterColor.colorValue;
            this.palette = masterColor.palette;
        } else {
            if (index > -1) {
                masterColor.dependentColors.splice(index, 1);
            }
            this.blendToBackground = false;
            this.setValueFromHex(colorHexValue);
        }
    }

    colorNoLongerDependent(colorCode:string) {
        this.dependentColors.splice(arrayIndexByKeyValue(this.dependentColors, "colorCode", colorCode), 1);
    }

    isHauptfarbe = ()=>this.isDependent === false && this.multipleMasters === false && this.isHideable === false;
    isInvisibleHauptfarbe = ()=>this.isHauptfarbe() && this.isVisible === false;
    isHightlight = ()=>this.colorCode.toLowerCase().lastIndexOf("high") === 0;
    isDependentWithoutCounterpart = ()=>this.isDependent === true && this.dependentOn === null;
    isOwayoOColorcode = ()=>this.colorCode.indexOf("OO") === 0;

    isConnectedToVisibleColorViaAlias(otherColorObjects:Color[]) {
        const colorCodesWithSameNameToDisplay = otherColorObjects.filter(c=>c.colorCode !== this.colorCode && c.colorCodeToDisplay === this.colorCodeToDisplay);
        return colorCodesWithSameNameToDisplay.some(c=>c.isVisible === true);
    }

    isUsedInCurrentDesign() {
        return !this.isDependentWithoutCounterpart()
            && !this.isHightlight()
            && !this.isInvisibleHauptfarbe()
            ;
    }

    private static hexStringIsNeutralColor(hexString) {
        const rgbObject = ColorUtils.hexToRgbObject(hexString) as {r,g,b};
        const durchschnitt = (rgbObject.r + rgbObject.g + rgbObject.b) / 3;

        let maxDelta = 0;

        maxDelta = Math.max(maxDelta, Math.abs(rgbObject.r - durchschnitt));
        maxDelta = Math.max(maxDelta, Math.abs(rgbObject.g - durchschnitt));
        maxDelta = Math.max(maxDelta, Math.abs(rgbObject.b - durchschnitt));
        return maxDelta <= CONSTANTS.NEUTRAL_COLOR_DELTA;
    }

    private static getIndexOfClosestNeutralColorInPalette(colorHexString, palette) {
        const luminance = ColorUtils.getLuminanceOfRgbObject(ColorUtils.hexToRgbObject(colorHexString));

        const minData = palette.reduce(
            function(carry, paletteColor, index) {
                if (Color.hexStringIsNeutralColor(paletteColor.RGBHex)) {
                    const deltaL = Math.abs(ColorUtils.getLuminanceOfRgbObject(ColorUtils.hexToRgbObject(paletteColor.RGBHex)) - luminance);
                    if (deltaL < carry.abstand) {
                        carry.abstand = deltaL;
                        carry.palettenIndex = index;
                    }
                }
                return carry;
            },
            {
                abstand: Infinity,
                palettenIndex: -1
            }
        );
        return minData.palettenIndex;
    }


    public isOutlineColorCode = ()=>this.colorCode.endsWith('_O');
}
